import { type MeResponse, TRANSPORT_DISPLAY_STATUSES, TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT, type TransportDisplayStatus, type TransportNocTripRow } from "@seagames/contracts";
import { Icons, KeysetPager, ListHeaderButton, ListToolbar, type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { nocTripsOptions } from "../../lib/api/transport-fleet";
import { transportSummaryOptions } from "../../lib/api/transport";
import { formatClockIn, zoneAbbreviation } from "../../lib/format/datetime";
import { type SearchNavigate, useListPage } from "../../lib/list-page/use-list-page";
import { nocSummary, statusCount, upcomingCount } from "../../lib/transport/view";
import { todayIn } from "../../lib/travel/view";
import { EmptyCard } from "../common/empty-card";
import { PhoneLink, VehicleTypeIcon, useCallingCodes } from "../transport-fleet";
import { PhoneIcon, RouteIcon, WheelchairIcon } from "../transport/icons";
import { TripSourceTag, TripStatusChip } from "../transport/status";
import { CancelTripModal } from "./cancel-trip-modal";
import { RequestTripModal } from "./request-trip-modal";
import { MetaLine, RouteLine, TimeCell, useFollowLabel } from "./trip-bits";

/**
 * A contingent's Transportation page (her NOC `#/ntr`, PLAN §4 "NOC view"):
 * the strip (the Upcoming list's counts by status, and today's done), "Your trips" Upcoming / Past (keyset, URL state), status
 * chips, Request a trip and Cancel. A NOC sees ONLY its own people on every
 * trip (PLAN §0b Q3, built in SQL): its own count, its own flights, the
 * vehicles and drivers — never a total, a seat count, another contingent or
 * a vehicle's load. Nothing here adds any of that back.
 *
 * S4's index route renders it for a session whose view is "noc", spreading
 * `nocTripsSearchShape` and passing `Route.useNavigate()`.
 */

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
/** What a NOC can see: SHORT is never sent to it (it would reveal other people). */
const NOC_STATUSES = TRANSPORT_DISPLAY_STATUSES.filter((s): s is Exclude<TransportDisplayStatus, "SHORT"> => s !== "SHORT");
const PAGE_SIZES = [25, 50, 100] as const;

export const nocTripsSearchShape = {
	trips: lenient(z.enum(["upcoming", "past"])),
	status: lenient(z.enum(TRANSPORT_DISPLAY_STATUSES)),
	limit: lenient(z.coerce.number().int().min(1).max(100)),
	cursor: lenient(z.string().min(1).max(200)),
	prev: lenient(z.array(z.string().min(1).max(200)).max(100)),
};
export type NocTripsSearch = z.infer<z.ZodObject<typeof nocTripsSearchShape>>;

export interface NocTripsViewProps {
	session: Pick<MeResponse, "active_role" | "group">;
	gamesId: string;
	/** The games' IANA zone: trip times are shown on its clock ("MYT"). */
	timeZone: string;
	search: NocTripsSearch;
	navigate: SearchNavigate<NocTripsSearch>;
}

export function NocTripsView({ session, gamesId, timeZone, search, navigate }: NocTripsViewProps) {
	const { t } = useTranslation("transportFleet");
	const { t: tt } = useTranslation("transport");
	const { formatNumber } = useUiStrings();
	const tab = search.trips ?? "upcoming";
	const today = todayIn(timeZone);
	const summary = useQuery(transportSummaryOptions(gamesId, today));
	const [requesting, setRequesting] = useState(false);
	const [cancelling, setCancelling] = useState<TransportNocTripRow | null>(null);

	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => nocTripsOptions({ games_id: gamesId, tab, status: search.status, cursor: q.cursor, limit: q.limit }),
		columnsKey: "transport-noc-trips",
		pageSizes: PAGE_SIZES,
		defaultPageSize: TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT,
		summary: {
			showing: (v) => t("trips.showing", v),
			showingNoTotal: (v) => t("trips.showingNoTotal", v),
			empty: t("trips.showingEmpty"),
		},
	});

	// A NOC reads only its own summary (`view: "noc"`, built from its own passengers; SHORT already folded in). The tiles count
	// what the default Upcoming list shows (same walk and status in SQL), so "Driver assigned 2" is the list's 2 such trips;
	// only "Done today" is the day's.
	const s = nocSummary(summary.data);
	const up = (k: Exclude<TransportDisplayStatus, "SHORT">) => (s ? upcomingCount(s, k) : 0);
	const cells: StatsCell[] = [
		{ key: "trips", label: t("noc.strip.upcoming"), value: s ? NOC_STATUSES.reduce((n, k) => n + up(k), 0) : "—", note: t("noc.strip.upcomingNote") },
		{ key: "waiting", label: t("noc.strip.waiting"), value: s ? up("PENDING") : "—", note: t("noc.strip.hostAssigns") },
		{ key: "assigned", label: t("noc.strip.assigned"), value: s ? up("ASSIGNED") : "—" },
		{ key: "road", label: t("noc.strip.onTheRoad"), value: s ? up("EN_ROUTE") + up("PARTIAL") : "—" },
		{ key: "done", label: t("noc.strip.doneToday"), value: s ? statusCount(s, "COMPLETED") : "—" },
	];

	const setTab = (next: "upcoming" | "past") => void navigate({ search: (p) => ({ ...p, trips: next === "upcoming" ? undefined : next, cursor: undefined, prev: undefined }), replace: true });

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<StatsStrip label={t("noc.strip.upcomingLabel")} cells={cells} className={summary.isFetching && s ? "opacity-60 transition-opacity" : "transition-opacity"} />

			<header className="flex flex-wrap items-center justify-between gap-x-[1rem] gap-y-[0.625rem]">
				<div className="min-w-0 flex-[1_1_18rem]">
					<h2 className="flex flex-wrap items-baseline gap-x-[0.5rem] text-[0.96875rem] leading-tight font-semibold text-gms-navy-ink">
						{t("noc.title")}
						{page.total !== null && <span className="text-[0.78125rem] font-normal text-gms-ink-3 tabular-nums">{t("noc.count", { count: page.total, n: formatNumber(page.total) })}</span>}
					</h2>
					<p className="mt-[0.25rem] text-[0.8125rem] leading-snug text-gms-ink-3">{t("noc.intro", { zone: zoneAbbreviation(timeZone) })}</p>
				</div>
				<ListHeaderButton tone="primary" icon={<Icons.Plus />} onClick={() => setRequesting(true)} className="max-[44.999rem]:w-full">
					{t("noc.request")}
				</ListHeaderButton>
			</header>

			<div className="flex flex-wrap items-center gap-[0.75rem]">
				<div role="tablist" aria-label={t("trips.tabsLabel")} className="inline-flex gap-[0.25rem] rounded-[0.625rem] bg-gms-soft-2 p-[0.25rem]">
					{(["upcoming", "past"] as const).map((k) => (
						<button
							key={k}
							type="button"
							role="tab"
							aria-selected={tab === k}
							onClick={() => tab !== k && setTab(k)}
							className={`inline-flex h-[2rem] min-w-[6.5rem] cursor-pointer items-center justify-center rounded-[0.5rem] px-[0.875rem] text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
								tab === k ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
							}`}
						>
							{t(`trips.tab.${k}`)}
						</button>
					))}
				</div>
				<ListToolbar
					className="min-w-0 flex-1"
					chips={[
						{
							kind: "options",
							key: "status",
							label: t("trips.statusChips"),
							options: NOC_STATUSES.map((st) => ({ value: st, label: tt(`status.label.${st}`) })),
							value: search.status ? [search.status] : [],
							onChange: (v) => page.patch({ status: v[0] ?? undefined }),
						},
					]}
					onClearAll={() => page.patch({ status: undefined })}
				/>
			</div>

			{page.list.isError ? (
				<EmptyCard
					tone="bad"
					title={t("trips.loadFailed")}
					actions={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void page.list.refetch()}>
							{t("common.retry")}
						</button>
					}
				>
					{page.table.error?.message}
				</EmptyCard>
			) : page.list.isPending ? (
				<div className="grid gap-[0.625rem]" aria-busy>
					<div className="skeleton h-[5.5rem] rounded-[0.625rem]" />
					<div className="skeleton h-[5.5rem] rounded-[0.625rem]" />
					<div className="skeleton h-[5.5rem] rounded-[0.625rem]" />
				</div>
			) : page.rows.length === 0 ? (
				<EmptyCard
					icon={<RouteIcon />}
					title={search.status ? t("noc.empty.filteredTitle") : tab === "past" ? t("noc.empty.pastTitle") : t("noc.empty.noneTitle")}
					actions={
						search.status ? (
							<button type="button" className="btn btn-sm btn-line" onClick={() => page.patch({ status: undefined })}>
								{t("common.clearFilters")}
							</button>
						) : tab === "upcoming" ? (
							<button type="button" className="btn btn-sm btn-primary" onClick={() => setRequesting(true)}>
								{t("noc.request")}
							</button>
						) : undefined
					}
				>
					{search.status ? t("noc.empty.filteredDescription") : tab === "past" ? t("noc.empty.pastDescription") : t("noc.empty.noneDescription")}
				</EmptyCard>
			) : (
				<>
					<ul className={`grid gap-[0.625rem] ${page.list.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}`}>
						{page.rows.map((r) => (
							<li key={r.transport_request_id}>
								<NocTripCard trip={r} timeZone={timeZone} past={tab === "past"} onCancel={() => setCancelling(r)} />
							</li>
						))}
					</ul>
					<KeysetPager {...page.pager} position="bottom" />
				</>
			)}

			{requesting && <RequestTripModal session={session} gamesId={gamesId} timeZone={timeZone} onClose={() => setRequesting(false)} />}
			{cancelling && <CancelTripModal trip={cancelling} onClose={() => setCancelling(null)} />}
		</div>
	);
}

/**
 * One trip (her `.l3trip2`). The count is the NOC's own people; vehicles
 * show plate and driver, never their load.
 */
function NocTripCard({ trip, timeZone, past, onCancel }: { trip: TransportNocTripRow; timeZone: string; past: boolean; onCancel: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const callingCodes = useCallingCodes();
	const closed = trip.state !== "OPEN";
	const live = trip.vehicles.filter((v) => v.state !== "CANCELLED");
	// "Follows TG 415" / "Follows Coach SG-1 (+2 h)" from its own flights AND services (E2E N1).
	const follows = useFollowLabel()(trip);

	return (
		<article
			className={`grid gap-x-[1rem] gap-y-[0.5rem] rounded-box border border-gms-line bg-base-100 px-[1rem] py-[0.75rem] sm:grid-cols-[5.5rem_minmax(0,1fr)_auto] ${past || closed ? "bg-gms-soft/60" : ""}`}
		>
			<TimeCell at={trip.pickup_at} timeZone={timeZone} muted={past || closed} />
			<div className="grid min-w-0 gap-[0.25rem]">
				<RouteLine from={trip.from.name} to={trip.to.name} />
				<MetaLine>
					{[
						<span key="ref" className="font-medium text-gms-ink-2">{trip.ref}</span>,
						<span key="pax">{t("trips.passengers", { count: trip.own_passengers, n: formatNumber(trip.own_passengers) })}</span>,
						trip.own_wheelchair > 0 ? (
							<span key="wc" className="inline-flex items-center gap-[0.25rem]" title={t("trips.wheelchairTitle")}>
								<WheelchairIcon className="size-[0.8125rem]" />
								{formatNumber(trip.own_wheelchair)}
							</span>
						) : null,
						trip.return_pickup_at ? <span key="ret">{t("trips.return", { time: formatClockIn(trip.return_pickup_at, timeZone) })}</span> : null,
						<TripSourceTag key="src" source={trip.source} />,
					]}
				</MetaLine>
				{follows && <span className="text-[0.78125rem] text-gms-ink-2">{follows}</span>}
				{trip.state_reason && closed && <span className="rounded-[0.375rem] bg-gms-warn-soft px-[0.5rem] py-[0.25rem] text-[0.78125rem] text-gms-warn">{t("trips.reason", { reason: trip.state_reason })}</span>}
				{live.length > 0 ? (
					<ul className="grid gap-[0.125rem]">
						{live.map((v) => (
							<li key={`${v.plate}-${v.driver_name}`} className="flex flex-wrap items-center gap-x-[0.375rem] text-[0.78125rem] text-gms-ink-2">
								<VehicleTypeIcon type={v.vehicle_type} className="size-[0.875rem] text-gms-ink-3" />
								<b className="font-semibold tracking-[0.02em] text-gms-navy-ink">{v.plate}</b>
								<span aria-hidden>·</span>
								<span>{v.driver_name}</span>
								<span aria-hidden>·</span>
								<span className="inline-flex items-center gap-[0.25rem]">
									<PhoneIcon className="size-[0.75rem] text-gms-ink-3" />
									<PhoneLink phone={v.driver_phone} callingCodes={callingCodes} />
								</span>
							</li>
						))}
					</ul>
				) : (
					trip.status === "PENDING" && <span className="text-[0.78125rem] text-gms-ink-3">{t("noc.waitingForHost")}</span>
				)}
			</div>
			<div className="flex flex-wrap items-start gap-[0.5rem] sm:flex-col sm:items-end">
				<TripStatusChip status={trip.status} />
				{trip.can_cancel ? (
					<button type="button" className="btn btn-line btn-sm text-gms-bad" onClick={onCancel}>
						{t("noc.cancel")}
					</button>
				) : (
					trip.source !== "ADHOC" &&
					!closed &&
					!past && <span className="max-w-[12rem] text-[0.75rem] text-gms-ink-3 sm:text-right">{t("noc.transferNote")}</span>
				)}
			</div>
		</article>
	);
}
